/**
 * #583 Refer a friend — invite landing capture + accept.
 *
 * Omneo's invite email links the friend to this site with
 * `?invite_id=<uuid>&email=<invited address>` (URL supplied to Omneo; any JAG
 * page works because this script is loaded from layout/theme.liquid). Flow:
 *
 *   1. Landing: keep `invite_id` (and the invited email) in first-party cookies
 *      for 30 days, so the friend can browse, register or log in later and still
 *      be attributed.
 *   2. Sign-in links get a `login_hint` with the invited address (the store uses
 *      Shopify's hosted customer accounts, so there is no theme register form
 *      to pre-fill). Omneo credits the referral ONLY if the friend joins with
 *      that exact email.
 *   3. Once signed in (script tag carries data-customer="true"): POST the id to
 *      jag-api (/apps/api/referrals/accept via the Shopify App Proxy, which
 *      attaches the Shopify-signed logged_in_customer_id). The worker verifies
 *      the email matches and accepts the invite with Omneo's machine token.
 *
 *   Response { ok, error?, terminal }  — `terminal` means nothing can succeed
 *   later for this invite + account (accepted, expired, wrong email…), so the
 *   cookie is dropped. Non-terminal (worker disabled, network) keeps the cookie
 *   and retries on the next page load.
 *
 * Fires `jag:referral:accepted` on document when Omneo confirms the accept.
 * Never sends or stores the customer id — identity is resolved server-side.
 */
(function () {
  'use strict';

  const ID_COOKIE = 'jag_invite_id';
  const EMAIL_COOKIE = 'jag_invite_email';
  const DONE_COOKIE = 'jag_invite_done';
  const DAYS = 30;
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  };
  const setCookie = (name, value, days) => {
    document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${days * 86400}; Path=/; SameSite=Lax; Secure`;
  };
  const clearCookie = (name) => {
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax; Secure`;
  };

  const params = new URLSearchParams(window.location.search);
  const paramId = (params.get('invite_id') || '').trim();
  const paramEmail = (params.get('email') || '').trim();

  let inviteId = '';
  if (UUID.test(paramId)) {
    inviteId = paramId;
    setCookie(ID_COOKIE, inviteId, DAYS);
    if (paramEmail) setCookie(EMAIL_COOKIE, paramEmail, DAYS);
  } else {
    inviteId = getCookie(ID_COOKIE);
    if (!UUID.test(inviteId)) inviteId = '';
  }

  if (!inviteId) return;

  const script = document.currentScript;
  const signedIn = !!script && script.dataset.customer === 'true';

  // The store uses Shopify's hosted customer accounts (no theme register page),
  // so the only pre-fill we can offer is a `login_hint` on the sign-in links.
  // Omneo credits the referral only when the friend joins with the invited
  // address, so nudging the same email into the sign-in screen matters.
  const invitedEmail = paramEmail || getCookie(EMAIL_COOKIE);
  if (!signedIn && invitedEmail) {
    document.querySelectorAll('a[href*="/customer_authentication/login"]').forEach((link) => {
      try {
        const url = new URL(link.getAttribute('href'), window.location.origin);
        if (!url.searchParams.has('login_hint')) {
          url.searchParams.set('login_hint', invitedEmail);
          link.setAttribute('href', url.pathname + url.search);
        }
      } catch {
        /* leave the link untouched */
      }
    });
  }

  if (!signedIn) return;
  if (getCookie(DONE_COOKIE) === inviteId) return;

  fetch('/apps/api/referrals/accept', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ invite_id: inviteId }),
  })
    .then((res) => res.json().catch(() => null))
    .then((body) => {
      if (!body) return;
      if (body.ok || body.terminal) {
        clearCookie(ID_COOKIE);
        clearCookie(EMAIL_COOKIE);
        // Remember the outcome so a terminal failure isn't re-posted every page view.
        setCookie(DONE_COOKIE, inviteId, DAYS);
      }
      if (body.ok) {
        document.dispatchEvent(new CustomEvent('jag:referral:accepted', { detail: { inviteId } }));
      }
    })
    .catch(() => {
      /* transient — keep the cookie and try again on the next page */
    });
})();
